import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface FeishuAuthState {
  feishuUserToken: string | null;
  /** Unix timestamp (ms) when the token expires. null if unknown. */
  feishuTokenExpiresAt: number | null;
  /** Feishu app credentials — user-configured, not platform-built-in. */
  feishuAppId: string;
  feishuAppSecret: string;
  feishuDevSpace: string;
  _hasHydrated: boolean;
  setFeishuUserToken: (token: string | null, expiresIn?: number) => void;
  setFeishuAppConfig: (appId: string, appSecret: string, devSpace: string) => void;
  setHasHydrated: (state: boolean) => void;
  clearFeishuToken: () => void;
  clearFeishuConfig: () => void;
  /** Check if the stored token is still valid (not expired). */
  isFeishuTokenValid: () => boolean;
  /** Check if feishu app credentials are configured. */
  isFeishuConfigured: () => boolean;
}

export const useFeishuAuthStore = create<FeishuAuthState>()(
  persist(
    (set, get) => ({
      feishuUserToken: null,
      feishuTokenExpiresAt: null,
      feishuAppId: '',
      feishuAppSecret: '',
      feishuDevSpace: '',
      _hasHydrated: false,
      setFeishuUserToken: (token, expiresIn) => {
        if (token === null) {
          set({ feishuUserToken: null, feishuTokenExpiresAt: null });
          return;
        }
        const expiresAt = expiresIn && expiresIn > 0 ? Date.now() + expiresIn * 1000 : null;
        set({ feishuUserToken: token, feishuTokenExpiresAt: expiresAt });
      },
      setFeishuAppConfig: (appId, appSecret, devSpace) =>
        set({ feishuAppId: appId, feishuAppSecret: appSecret, feishuDevSpace: devSpace }),
      setHasHydrated: (state) => set({ _hasHydrated: state }),
      clearFeishuToken: () => {
        set({ feishuUserToken: null, feishuTokenExpiresAt: null });
      },
      clearFeishuConfig: () => {
        set({ feishuAppId: '', feishuAppSecret: '', feishuDevSpace: '' });
      },
      isFeishuTokenValid: () => {
        const { feishuUserToken, feishuTokenExpiresAt } = get();
        if (!feishuUserToken || feishuUserToken.trim() === '') return false;
        // If we don't know the expiry, assume it's still valid (user must manually re-login on failure).
        if (feishuTokenExpiresAt === null) return true;
        // Consider token expired 60s early to avoid edge cases.
        return Date.now() < feishuTokenExpiresAt - 60_000;
      },
      isFeishuConfigured: () => {
        const { feishuAppId, feishuAppSecret } = get();
        return feishuAppId.trim().length > 0 && feishuAppSecret.trim().length > 0;
      },
    }),
    {
      name: 'feishu-auth-storage',
      partialize: (state) => ({
        feishuUserToken: state.feishuUserToken,
        feishuTokenExpiresAt: state.feishuTokenExpiresAt,
        feishuAppId: state.feishuAppId,
        feishuAppSecret: state.feishuAppSecret,
        feishuDevSpace: state.feishuDevSpace,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
